/**
 * TideMirror.ts
 * Crystal-clone miniboss for World 5 (Eclipse Bastion).
 *
 * Behaviour: real-time horizontal mirror of Nova around a fixed arena
 * axis. When Nova moves right the Mirror moves left, and vice versa —
 * the two are always reflected across `_axisX`. Periodically the Mirror
 * pauses to telegraph a reflected beam attack ("the tell"); that
 * telegraph is also the only window in which the Mirror is vulnerable.
 * On telegraph exit, a single-tick `fireRequested` pulse signals the
 * level combat layer to spawn the beam itself.
 *
 * State machine: MIRRORING → TELL → MIRRORING
 *                 (with HP=0 at any TELL hit transitioning to DEAD)
 *
 * Differs from `ShadowGlider` (which delays Nova's recorded position
 * by 2 s) — TideMirror is instantaneous and horizontally inverted,
 * not a time-shifted replay.
 *
 * Rules (AGENTS.md):
 *   - No React imports. No async. No Skia imports.
 *   - Extends BaseEntity.
 *   - AI state transitions logged at DEBUG level.
 *
 * Phase 4 — World 5 miniboss greybox stub. Wired into levels when W5 ships.
 * See ROADMAP.md Phase 4 (`TideMirror.ts`).
 *
 * NOTE: The beam projectile itself is not implemented here — the level
 * combat layer will consume the single-tick `fireRequested` pulse and
 * spawn a beam targeted at Nova's then-current position.
 *
 * @module TideMirror
 */

import { BaseEntity } from '../BaseEntity';
import type { LevelRuntime, NovaRuntime } from '../../engine/gameState';
import { resolveAllPlatforms } from '../../engine/collision';
import { GRAVITY, MAX_FALL_SPEED } from '../../engine/physics';
import Logger from '../../utils/logger';

export type TideMirrorState = 'MIRRORING' | 'TELL' | 'DEAD';

/**
 * Projectile-impact result. `BLOCKED` while MIRRORING (crystal armour up);
 * `HURT` only fires inside the TELL window.
 */
export type TideMirrorHitResult = 'BLOCKED' | 'HURT' | 'IGNORED';

/** Width/height of the Mirror hitbox in logical pixels — Nova-clone sized. */
export const TIDE_MIRROR_WIDTH = 22;
export const TIDE_MIRROR_HEIGHT = 30;
/** Greybox render colour — crystal cyan. */
export const TIDE_MIRROR_COLOR = '#9fc4d8';

/** HP pool — three connected tell-window hits to kill. */
export const TIDE_MIRROR_MAX_HP = 3;

/** Time between beam tells while MIRRORING. */
const MIRROR_DURATION = 5.0;
/** Telegraph length — also the vulnerability window. */
const TELL_DURATION = 0.8;

// DECISION: Reusable hitbox object to avoid per-tick allocations in the
// engine hot path (mirrors Gearling / BrambleRam / ForgeWarden).
const _hitbox = { x: 0, y: 0, width: TIDE_MIRROR_WIDTH, height: TIDE_MIRROR_HEIGHT };

/**
 * TideMirror — World 5 miniboss (Eclipse Bastion).
 *
 * Spawn with `new TideMirror(x, y, axisX?)`. `axisX` is the vertical line
 * around which the Mirror reflects Nova; defaults to the spawn `x` (use
 * the arena centreline in real levels). Call `update(dt, level, nova?)`
 * each fixed tick. Read `fireRequested` for the single-tick beam pulse,
 * `dead` to know when to remove, and `hp` for HUD purposes.
 */
export class TideMirror extends BaseEntity {
  public readonly entityType = 'TideMirror';
  public readonly color = TIDE_MIRROR_COLOR;
  public readonly maxHp = TIDE_MIRROR_MAX_HP;
  public state: TideMirrorState = 'MIRRORING';

  /**
   * True once dead. TideMirror has no intermediate death animation — the
   * fatal hit sets `DEAD` directly. See `Enemy.isDying`.
   */
  public get isDying(): boolean {
    return this.state === 'DEAD';
  }
  /** Remaining HP. Decremented only by hits landed during TELL. */
  public hp = TIDE_MIRROR_MAX_HP;
  /** True when facing right (used by renderer for sprite flip). */
  public facingRight = false;
  /** Single-tick pulse — true on the tick the Mirror exits TELL. */
  public fireRequested = false;
  /** Reflection axis. Mirror.x = 2 * _axisX − nova.x. */
  private readonly _axisX: number;
  private _mirrorTimer = MIRROR_DURATION;
  private _tellTimer = 0;

  constructor(x: number, y: number, axisX: number = x) {
    super(x, y, TIDE_MIRROR_WIDTH, TIDE_MIRROR_HEIGHT);
    this._axisX = axisX;
  }

  /**
   * Advance the Mirror by one fixed tick.
   * @param dt    Fixed-step delta in seconds.
   * @param level Current level geometry — read-only.
   * @param nova  Optional Nova runtime snapshot. The Mirror tracks Nova's
   *              x coordinate when provided; without one it freezes.
   */
  public update(dt: number, level: LevelRuntime, nova?: NovaRuntime): void {
    // DECISION: Clear the pulse at the top of every tick. _enterMirroring()
    // sets it back to true on the same tick it fires, giving a clean
    // single-tick pulse the level combat layer can consume.
    this.fireRequested = false;
    if (this.state === 'DEAD') return;

    this.vy += GRAVITY * dt;
    this.vy = Math.min(this.vy, MAX_FALL_SPEED);

    // DECISION: Without a Nova snapshot the beam cycle pauses entirely —
    // no timer ticks, no state transitions, no fireRequested pulse. Gravity
    // and collision still run so the Mirror falls/settles cleanly. This
    // matches the docstring contract that callers can omit `nova` to
    // freeze the entity (e.g. between scene transitions).
    switch (this.state) {
      case 'MIRRORING':
        if (nova) {
          this._snapToMirror(nova);
          this._mirrorTimer -= dt;
          if (this._mirrorTimer <= 0) this._enterTell();
        }
        this._integrateYAndResolve(dt, level);
        break;
      case 'TELL':
        // Frozen — no movement during the telegraph. Gravity still applies
        // so a Mirror knocked into mid-air falls cleanly.
        if (nova) {
          this._tellTimer -= dt;
          if (this._tellTimer <= 0) this._enterMirroring();
        }
        this._integrateYAndResolve(dt, level);
        break;
    }
  }

  /**
   * Snap the Mirror's x to Nova's reflection across `_axisX`, and lock
   * facing toward the axis (the Mirror always looks at where Nova is).
   * Y is intentionally not mirrored — the Mirror walks on its own floor.
   */
  private _snapToMirror(nova: NovaRuntime): void {
    this.x = 2 * this._axisX - nova.x;
    this.facingRight = nova.x >= this._axisX;
  }

  /**
   * Integrate the y component and resolve platform overlaps. The mirror
   * equation owns x in the common case, but if the snapped x penetrates a
   * solid tile the resolver's horizontal depenetration is accepted so the
   * Mirror can't get stuck inside geometry (the reflection invariant is
   * sacrificed for that tick — graceful degradation when an arena puts
   * walls inside the reflection swing).
   */
  private _integrateYAndResolve(dt: number, level: LevelRuntime): void {
    this.y += this.vy * dt;
    _hitbox.x = this.x;
    _hitbox.y = this.y;
    const r = resolveAllPlatforms(this.x, this.y, _hitbox, level.platforms, this.vy);
    // DECISION: Accept r.x — when the resolver reports no horizontal hit
    // it equals the snapped x already, so this is a no-op in the common
    // case. When a side hit occurs (hitLeft/hitRight), this pushes the
    // Mirror out of the wall instead of leaving it embedded.
    this.x = r.x;
    this.y = r.y;
    if (r.landedOnTop) this.vy = 0;
  }

  private _enterTell(): void {
    this.state = 'TELL';
    this._tellTimer = TELL_DURATION;
    Logger.debug('TideMirror', 'Beam tell → TELL (vulnerable)');
  }

  private _enterMirroring(): void {
    this.state = 'MIRRORING';
    this._mirrorTimer = MIRROR_DURATION;
    this.fireRequested = true; // single-tick pulse on tell exit
    Logger.debug('TideMirror', 'Tell complete → MIRRORING (beam fires)');
  }

  /**
   * Apply one projectile hit.
   * @returns `'BLOCKED'` while MIRRORING, `'HURT'` while TELL,
   *          `'IGNORED'` once DEAD.
   */
  public applyShardHit(): TideMirrorHitResult {
    if (this.state === 'DEAD') return 'IGNORED';
    if (this.state !== 'TELL') return 'BLOCKED';
    this._takeDamage();
    return 'HURT';
  }

  /**
   * Apply one stomp hit. Armoured from above while MIRRORING.
   * @returns `true` when the stomp connected (HP decremented); `false` otherwise.
   */
  public applyStomp(): boolean {
    if (this.state === 'DEAD') return false;
    if (this.state !== 'TELL') return false;
    this._takeDamage();
    return true;
  }

  /**
   * Shared `Enemy` contract adapter (see gameStateTypes.ts).
   * @param fromStomp `true` for stomp contact, `false`/omitted for shards.
   * @returns `true` if this hit dealt damage; `false` if blocked or ignored.
   */
  public applyHit(fromStomp = false): boolean {
    if (fromStomp) return this.applyStomp();
    return this.applyShardHit() === 'HURT';
  }

  /**
   * Deduct one HP and route to DEAD when the pool empties. Called only
   * from a successful TELL-state hit.
   */
  private _takeDamage(): void {
    this.hp -= 1;
    Logger.debug('TideMirror', `Hit landed — HP ${this.hp}/${this.maxHp}`);
    if (this.hp <= 0) {
      this.state = 'DEAD';
      this.dead = true;
      Logger.debug('TideMirror', 'Defeated');
    }
  }
}
